/**
 * クラウドサインの利用権限（HubSpot ユーザー／チーム × クライアントID）。
 *
 * 契約書の送信は影響が大きいため、テナントごとに 2 つのモードを選べる。
 *
 *   全員可         … 割り当てが無い人は全クライアントIDを使える（従来の挙動）
 *   許可した人のみ … チェックした人／チームだけが使える
 *
 * クライアントIDは列に並べず、行ごとのドロップダウンで複数選択する
 * （列だとIDが増えたとき横に詰む）。
 *
 * 「選択 → まとめて設定」ができる。多数のユーザーに同じ設定をするため。
 *
 * 「許可済みでクライアントIDを選んでいない」= 全クライアントID可。
 * 保存は 1 リクエストのバッチ（全選択で数十行を書き換えるため）。
 */
import { useState, useEffect, useMemo } from "react";
import { api, type PermissionState } from "../api.js";
import { card, inputStyle, btnPrimary, btnSecondary, SectionTitle, Badge } from "./ui.js";
import ClientIdMultiSelect from "./ClientIdMultiSelect.js";
import {
  emptyPermissionState,
  toggleAllowed,
  setAllAllowed,
  assignClientIdsTo,
  isAllowed,
  describeRow,
  type Subject,
} from "./permissionMatrix.js";

interface HubSpotUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}
interface HubSpotTeam {
  id: string;
  name: string;
  memberCount: number;
}
interface ClientIdRow {
  id: string;
  label: string;
  environment: string;
}

export default function HubSpotUsersTab() {
  const [users, setUsers] = useState<HubSpotUser[]>([]);
  const [teams, setTeams] = useState<HubSpotTeam[]>([]);
  const [teamsNeedReauth, setTeamsNeedReauth] = useState(false);
  const [clientIds, setClientIds] = useState<ClientIdRow[]>([]);
  const [state, setState] = useState<PermissionState>(emptyPermissionState());
  const [subject, setSubject] = useState<Subject>("users");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkClientIds, setBulkClientIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [u, t, c, s] = await Promise.all([
          api.getHubSpotUsers(),
          api.getHubSpotTeams().catch(() => ({ teams: [], needsReauth: true })),
          api.getClientIds(),
          api.getPermissionState(),
        ]);
        setUsers(u.users || []);
        setTeams(t.teams || []);
        setTeamsNeedReauth(!!t.needsReauth);
        setClientIds(c.clientIds || []);
        setState(s);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // タブ切替・検索が変わったら選択をリセット（別の対象に選択が残ると事故る）
  useEffect(() => {
    setSelected(new Set());
  }, [subject]);

  const update = (next: PermissionState) => {
    setState(next);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.savePermissionState(state);
      setSaved(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const allRows: Array<{ id: string; primary: string; secondary: string }> = useMemo(() => {
    if (subject === "users") {
      return users.map((u) => ({
        id: u.id,
        primary: `${u.lastName || ""} ${u.firstName || ""}`.trim() || u.email,
        secondary: u.email,
      }));
    }
    return teams.map((t) => ({
      id: t.id,
      primary: t.name,
      secondary: `${t.memberCount} 名`,
    }));
  }, [subject, users, teams]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) => r.primary.toLowerCase().includes(q) || r.secondary.toLowerCase().includes(q),
    );
  }, [allRows, search]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;

  const restricted = state.mode === "restricted";
  const options = clientIds.map((c) => ({ id: c.id, label: c.label, environment: c.environment }));
  const visibleIds = rows.map((r) => r.id);
  const selectedIds = visibleIds.filter((id) => selected.has(id));
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  };

  // 選択行への一括適用
  const bulkAllow = (allow: boolean) => {
    update(setAllAllowed(state, subject, selectedIds, allow));
  };
  const bulkAssign = () => {
    update(assignClientIdsTo(state, subject, selectedIds, bulkClientIds));
  };

  return (
    <>
      <SectionTitle>クラウドサインの利用権限</SectionTitle>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* ── モード ── */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>誰がクラウドサインを使えるか</div>
        <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 16, lineHeight: 1.7 }}>
          契約書の送信は影響が大きいため、利用できる人を絞ることをおすすめします。
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            {
              value: "open" as const,
              title: "全員が利用できる",
              desc: "HubSpot の全ユーザーが送信できます。下の表でクライアントIDを絞ることはできますが、絞らなかった人は全クライアントIDを使えます。",
            },
            {
              value: "restricted" as const,
              title: "許可した人だけが利用できる（推奨）",
              desc: "下の表でチェックした人・チームだけが送信できます。チェックしていない人はカードにボタンが表示されず、送信も拒否されます。",
            },
          ].map((opt) => (
            <div
              key={opt.value}
              onClick={() => update({ ...state, mode: opt.value })}
              style={{
                display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                border: state.mode === opt.value ? "2px solid #534AB7" : "1px solid #e8e7e3",
                background: state.mode === opt.value ? "#EEEDFE" : "white",
              }}
            >
              <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${state.mode === opt.value ? "#534AB7" : "#ccc"}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, flexShrink: 0 }}>
                {state.mode === opt.value && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7" }} />}
              </span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{opt.title}</div>
                <div style={{ fontSize: 13, color: "#6b6b68", marginTop: 3, lineHeight: 1.7 }}>{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ユーザー / チーム ── */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", border: "1px solid #e8e7e3", borderRadius: 8 }}>
            {([
              { k: "users" as const, label: `ユーザー（${users.length}）` },
              { k: "teams" as const, label: `チーム（${teams.length}）` },
            ]).map((t, i) => (
              <button
                key={t.k}
                onClick={() => setSubject(t.k)}
                style={{
                  padding: "7px 16px", fontSize: 13, border: "none", cursor: "pointer",
                  background: subject === t.k ? "#534AB7" : "white",
                  color: subject === t.k ? "white" : "#6b6b68",
                  fontWeight: subject === t.k ? 600 : 400,
                  borderRadius: i === 0 ? "8px 0 0 8px" : "0 8px 8px 0",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            style={{ ...inputStyle, flex: 1, minWidth: 200, maxWidth: 320 }}
            type="text"
            placeholder={subject === "users" ? "名前・メールで検索" : "チーム名で検索"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {subject === "teams" && teamsNeedReauth && (
          <div style={{ padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8, marginBottom: 14 }}>
            <b>チーム一覧を取得できません。</b>
            <br />
            チーム単位の権限を使うには HubSpot の再接続が必要です。「接続設定」から接続し直してください。
            <b>再接続しても既存の設定は保持されます。</b>
            再接続しない場合もユーザー単位の権限は通常どおり使えます。
          </div>
        )}

        {/* ── 選択中の一括操作バー ── */}
        {selectedIds.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 16px", background: "#EEEDFE", borderRadius: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#534AB7" }}>{selectedIds.length} 件を選択中</span>
            {restricted && (
              <>
                <button style={{ ...btnSecondary, fontSize: 12, padding: "6px 12px" }} onClick={() => bulkAllow(true)}>
                  許可する
                </button>
                <button style={{ ...btnSecondary, fontSize: 12, padding: "6px 12px" }} onClick={() => bulkAllow(false)}>
                  解除する
                </button>
                <span style={{ width: 1, height: 20, background: "#d0cfc9" }} />
              </>
            )}
            <span style={{ fontSize: 12, color: "#6b6b68" }}>クライアントIDを</span>
            <div style={{ minWidth: 220 }}>
              <ClientIdMultiSelect options={options} selected={bulkClientIds} onChange={setBulkClientIds} />
            </div>
            <button style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }} onClick={bulkAssign}>
              選択中に割り当て
            </button>
          </div>
        )}

        {rows.length === 0 ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "#6b6b68", fontSize: 14 }}>
            {search ? "該当がありません。" : subject === "users" ? "HubSpot ユーザーが取得できませんでした。" : "チームがありません。"}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ ...th(), width: 36 }}>
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                  </th>
                  <th style={th()}>{subject === "users" ? "ユーザー" : "チーム"}</th>
                  {restricted && <th style={{ ...th(), width: 80, textAlign: "center" }}>利用を許可</th>}
                  <th style={{ ...th(), minWidth: 240 }}>利用できるクライアントID</th>
                  <th style={{ ...th(), minWidth: 140 }}>状態</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const allowed = isAllowed(state, subject, r.id);
                  const disabled = restricted && !allowed;
                  return (
                    <tr key={r.id} style={{ borderTop: "1px solid #f0efeb" }}>
                      <td style={{ ...td(), textAlign: "center" }}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />
                      </td>
                      <td style={td()}>
                        <div style={{ fontWeight: 500 }}>{r.primary}</div>
                        <div style={{ fontSize: 12, color: "#6b6b68" }}>{r.secondary}</div>
                      </td>
                      {restricted && (
                        <td style={{ ...td(), textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={allowed}
                            onChange={(e) => update(toggleAllowed(state, subject, r.id, e.target.checked))}
                          />
                        </td>
                      )}
                      <td style={td()}>
                        <ClientIdMultiSelect
                          options={options}
                          selected={state[subject].clientIds[r.id] ?? []}
                          disabled={disabled}
                          onChange={(next) => update(assignClientIdsTo(state, subject, [r.id], next))}
                        />
                      </td>
                      <td style={{ ...td(), fontSize: 12, color: "#6b6b68" }}>
                        {describeRow(state, subject, r.id, clientIds.length)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 16, padding: "12px 16px", background: "#faf9f7", borderRadius: 8, fontSize: 12, color: "#6b6b68", lineHeight: 1.8 }}>
          クライアントIDを1つも選ばない場合は<b>すべてのクライアントIDが使えます</b>。
          特定のクライアントIDに限定したいときだけ選んでください。
          <br />
          左のチェックで複数選び、上のバーからまとめて設定できます。
          ユーザーとチームの両方で許可されている場合は<b>両方の合計</b>が使えます
          （チームは主所属・副所属の両方が対象）。
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
          {saving ? "保存中..." : "権限設定を保存"}
        </button>
        {saved && <span style={{ fontSize: 14, color: "#0F6E56", fontWeight: 600 }}>保存しました</span>}
        {restricted && <Badge bg="#E1F5EE" color="#0F6E56">許可した人のみ</Badge>}
      </div>
    </>
  );
}

const th = (): React.CSSProperties => ({
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 12,
  color: "#6b6b68",
  fontWeight: 600,
  background: "#fbfaf8",
  verticalAlign: "bottom",
});

const td = (): React.CSSProperties => ({ padding: "12px", verticalAlign: "middle" });
